// App.js — Version 2.9.5
// ✅ Wind column MSW-style (speed / gust + colour bar)
// ✅ Weather icons, secondary swell, 3-hour filter
// ✅ SwellArrow wrapper fixes 45° offset once, so icon swaps are painless
// ✅ Added min-h-20 to, and removed py-3 from, line 104 (Row wrapper)
// ✅ Added border-gray-800/20 to line 106 (Row wrapper). The /20 means 20 % Opaque = 80 % Transparent
/* ── imports ───────────────────────────────────────────────────────── */

import SearchForecast from "./SearchForecast"; //Imports SearchForecast.js which contains the predictive beach search logic.

import React, { useEffect, useState } from "react";
import {
  WiDaySunny,
  WiCloudy,
  WiCloud,
  WiRain,
  WiSnow,
  WiShowers,
} from "react-icons/wi";
import { FaArrowUp, FaLocationArrow } from "react-icons/fa";

/* ── Swell arrow wrapper (handles −45° correction) ─────────────────── */
const SwellArrow = ({ deg, className = "" }) => (
  <FaLocationArrow
    className={className}
    style={{ transform: `rotate(${deg - 45}deg)` }} // one-time offset
  />
);

/* ── constants & look-ups ──────────────────────────────────────────── */
const WEBHOOK_URL =
  "https://n8n.193.122.13.148.nip.io/webhook/getJSONForecast";

const weatherMap = {
  0: { icon: <WiDaySunny />, desc: "Clear" },
  1: { icon: <WiDaySunny />, desc: "Mainly clear" },
  2: { icon: <WiCloud />, desc: "Partly cloudy" },
  3: { icon: <WiCloudy />, desc: "Overcast" },
  45: { icon: <WiCloud />, desc: "Fog" },
  51: { icon: <WiShowers />, desc: "Light drizzle" },
  61: { icon: <WiRain />, desc: "Rain" },
  71: { icon: <WiSnow />, desc: "Snow" },
  80: { icon: <WiShowers />, desc: "Light showers" },
};

const useMetric = true; // flip when you add a units toggle
const kmhToMph = (k) => (k != null ? Math.round(k * 0.621371) : null);
const fmtSpeed = (k) => (useMetric ? k?.toFixed(0) : kmhToMph(k));
const unitLbl = useMetric ? "km/h" : "mph";
/* 🧮 conversions */
const mToFt = (m) => (m != null ? (m * 3.28084).toFixed(1) : null);

/* Pretty-print wave height */
const fmtWave = (m) =>
  useMetric
    ? m != null
      ? `${m.toFixed(2)} m`
      : "Flat"
    : m != null
      ? `${mToFt(m)} ft`
      : "Flat";

const hourLbl = (iso) =>
  new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
const dateLbl = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" });
const stars = (n) => "★".repeat(n) + "☆".repeat(5 - n);
const windBg = (c) =>
  c === "green" ? "bg-green-500"
  : c === "orange" ? "bg-orange-400"
  : c === "red" ? "bg-red-500"
  : "bg-gray-400";

/* ── component ─────────────────────────────────────────────────────── */
const ForecastTable = () => {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);          // reserve for future API errors

  /* data3h derives from whatever SearchForecast loads */
  const data3h = rows.filter((r) => {
    const h = new Date(r.Time).getHours();
    return h % 3 === 0 && h <= 21;
  });

  return (
    <div className="max-w-6xl mx-auto p-4 font-sans">
      <h2 className="text-2xl font-bold text-center mb-4">
        Supertubos Surf Forecast
      </h2>

      {/* 🔍 predictive search bar */}
      <SearchForecast
        onForecast={(data) => setRows(Array.isArray(data) ? data : [])}
      />

      {/* placeholders / errors */}
      {err && <p className="text-red-600 text-center">{err}</p>}
      {!rows.length && (
        <p className="text-gray-400 text-center">
          Select a beach to load its forecast…
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          {/* headers */}
          <div className="grid grid-cols-8 gap-2 font-semibold text-base bg-gray-100 px-2 py-3 text-center uppercase">
            <div>Time</div>
            <div>Wave</div>
            <div>Rating</div>
            <div>Primary Swell</div>
            <div>Secondary Swell</div>
            <div>Wind</div>
            <div>Weather</div>
            <div>Prob.</div>
          </div>

          {/* date row */}
          <div className="bg-gray-900 text-left text-gray-100 text-base px-2 py-2 font-medium">
            {dateLbl(data3h[0].Time)}
          </div>

          {/* rows */}
          {data3h.map((s, i) => {
            const w =
              weatherMap[parseInt(s.WeatherCondition ?? -1, 10)] ?? {
                icon: <WiCloud />,
                desc: "N/A",
              };
            const spd = fmtSpeed(s.WindSpeed);
            const gst = fmtSpeed(s.WindGust);

            return (
              /* Row wrapper */
              <div
                key={i}
                className={`grid grid-cols-8 gap-2 items-center px-2 text-sm text-center min-h-20
                  ${i % 2 ? "bg-gray-50" : "bg-white"} border-b border-gray-800/20`}
              >
                {/* Time */}
                <div className="font-medium text-base">{hourLbl(s.Time)}</div>

                {/* Wave height */}
                <div
                  className="bg-cyan-700 text-white font-bold text-base flex items-center justify-center w-full h-full"
                  title={s.WaveHeightText || "n/a"}
                >
                  {fmtWave(s.WaveHeight)}
                </div>

                {/* Stars */}
                <div className="text-yellow-500 text-lg">
                  {stars(s.SurflineStars)}
                </div>

                {/* Primary Swell */}
                <div className="flex items-center justify-center gap-2.5 text-base">
                  <span>{s.SwellHeight ? `${s.SwellHeight} m` : "–"}</span>
                  <span>{s.SwellPeriod ? `${s.SwellPeriod} s` : "–"}</span>
                  {s.SwellDirection != null ? (
                    <span title={s.SwellDirectionText}>
                      <SwellArrow
                        deg={s.SwellDirection}
                        className="text-blue-500 text-lg leading-none"
                      />
                    </span>
                  ) : (
                    "–"
                  )}
                </div>

                {/* Secondary Swell */}
                <div className="flex items-center justify-center gap-2.5 text-base">
                  <span>
                    {s.SwellHeightSecondary
                      ? `${s.SwellHeightSecondary} m`
                      : "–"}
                  </span>
                  <span>
                    {s.SwellPeriodSecondary
                      ? `${s.SwellPeriodSecondary} s`
                      : "–"}
                  </span>
                  {s.SwellDirectionSecondary != null ? (
                    <span title={s.SwellDirSecondaryText}>
                      <SwellArrow
                        deg={s.SwellDirectionSecondary}
                        className="text-blue-500 text-lg leading-none"
                      />
                    </span>
                  ) : (
                    "–"
                  )}
                </div>

                {/* Wind */}
                <div
                  className="flex h-full"
                  title={`${s.WindStrengthLabel} ${s.WindRelativeDirectionLabel}, ${s.WindDirectionText}`}
                >
                  <div className="flex items-center pl-2 w-20">
                    <span className="font-semibold text-2xl mr-1">
                      {spd ?? "–"}
                    </span>
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-xs leading-tight">{gst}</span>
                      <span className="text-xs leading-tight">{unitLbl}</span>
                    </div>
                  </div>
                  <div
                    className={`flex-1 flex items-center justify-center ${windBg(
                      s.WindRelativeColor
                    )}`}
                  >
                    {s.WindDirection != null ? (
                      <FaArrowUp
                        className="text-white text-lg leading-none"
                        style={{ transform: `rotate(${s.WindDirection}deg)` }}
                      />
                    ) : (
                      "–"
                    )}
                  </div>
                </div>

                {/* Weather */}
                <div
                  className="flex items-center justify-center gap-10"
                  title={w.desc}
                >
                  <span className="text-3xl">{w.icon}</span>
                  <span className="text-xl">
                    {s.AirTemperature != null
                      ? `${s.AirTemperature.toFixed(0)}°`
                      : "–"}
                  </span>
                </div>

                {/* Probability */}
                <div>{s.ForecastProbText || "–"}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ForecastTable;
